import { makeStyles, tokens } from '@fluentui/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { getFaviconUrl } from './sourceUtils';

// Single favicon component shared by the inline SourceChip and the SourcesDropdown so the loading,
// failed-state reset, letter fallback and `referrerPolicy` live in one place and can't drift between
// the two surfaces (the reason sourceUtils.ts exists). Only the size/styling differs per surface,
// selected via the `variant` prop.

const fallbackBase = {
    backgroundColor: 'var(--app-bot-icon-background, ' + tokens.colorBrandBackground2 + ')',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
} as const;

const useClasses = makeStyles({
    chipImg: {
        width: '12px',
        height: '12px',
        borderRadius: '2px',
        objectFit: 'contain',
    },
    chipFallback: {
        ...fallbackBase,
        width: '12px',
        height: '12px',
        borderRadius: '2px',
        fontSize: '7px',
        fontWeight: tokens.fontWeightBold,
    },
    popoverImg: {
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        objectFit: 'contain',
    },
    popoverFallback: {
        ...fallbackBase,
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        fontSize: '10px',
        fontWeight: tokens.fontWeightBold,
    },
    dropdownImg: {
        width: '22px',
        height: '22px',
        borderRadius: tokens.borderRadiusSmall,
        objectFit: 'contain',
    },
    dropdownFallback: {
        ...fallbackBase,
        width: '22px',
        height: '22px',
        borderRadius: tokens.borderRadiusSmall,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
    },
});

export type SourceFaviconVariant = 'chip' | 'popover' | 'dropdown';

const sizePx: Record<SourceFaviconVariant, number> = { chip: 12, popover: 18, dropdown: 22 };

// Glyph is sized a little smaller than its fallback tile so it reads as an icon with breathing room
// rather than filling edge-to-edge.
const glyphPx: Record<SourceFaviconVariant, number> = { chip: 10, popover: 13, dropdown: 16 };

// Neutral "generic source" mark for citations that have no URL/hostname — e.g. internal knowledge
// like bookkeeping-book concepts. A globe is the conventional stand-in; it replaces the earlier
// literal "?", which read as a broken/encoding glitch rather than a legitimate no-icon state (#2345).
// Decorative only (aria-hidden); inherits `currentColor` so it stays theme-correct in light/dark.
const GenericSourceGlyph: React.FC<{ size: number }> = ({ size }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable={false}
        data-testid="source-favicon-globe"
    >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3c2.5 2.6 3.9 5.7 3.9 9s-1.4 6.4-3.9 9c-2.5-2.6-3.9-5.7-3.9-9s1.4-6.4 3.9-9z" />
    </svg>
);

interface SourceFaviconProps {
    hostname: string;
    variant: SourceFaviconVariant;
}

export const SourceFavicon: React.FC<SourceFaviconProps> = ({ hostname, variant }) => {
    const classes = useClasses();
    const [failed, setFailed] = useState(false);

    // Reset the failed state when the hostname changes so a previously-failed favicon doesn't keep
    // showing the letter fallback for an unrelated source (the same instance is reused as the
    // popover navigates between sources).
    useEffect(() => {
        setFailed(false);
    }, [hostname]);

    const onError = useCallback(() => {
        setFailed(true);
    }, []);

    const imgClass =
        variant === 'chip' ? classes.chipImg : variant === 'popover' ? classes.popoverImg : classes.dropdownImg;
    const fallbackClass =
        variant === 'chip'
            ? classes.chipFallback
            : variant === 'popover'
              ? classes.popoverFallback
              : classes.dropdownFallback;

    // No hostname at all (the source has no URL) → neutral globe glyph. A failed favicon load for a
    // known host still falls back to the host's first letter, which is a useful identity cue.
    if (!hostname) {
        return (
            <div className={fallbackClass}>
                <GenericSourceGlyph size={glyphPx[variant]} />
            </div>
        );
    }

    if (failed) {
        return <div className={fallbackClass}>{hostname[0].toUpperCase()}</div>;
    }

    return (
        <img
            className={imgClass}
            data-testid="source-favicon-img"
            src={getFaviconUrl(hostname)}
            alt=""
            width={sizePx[variant]}
            height={sizePx[variant]}
            onError={onError}
            referrerPolicy="no-referrer"
        />
    );
};
